import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '@/i18n';
import './dropdown.css';

export type DropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

type PortalRect = { left: number; top: number; width: number };

type DropdownProps = {
  id?: string;
  label?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (nextOpen: boolean) => void;
  onToggle?: (nextOpen: boolean) => void;
  menuPlacement?: 'above' | 'below';
  usePortal?: boolean;
  portalRect?: PortalRect | null;
  portalRef?: React.RefObject<HTMLDivElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  menuClassName?: string;
  renderToggle?: (args: {
    isOpen: boolean;
    disabled?: boolean;
    display: string;
    placeholder: string;
    toggleProps: React.HTMLAttributes<HTMLElement>;
    openMenu: () => void;
    closeMenu: () => void;
    toggleMenu: () => void;
  }) => React.ReactNode;
};

const renderDropdownLabel = (label: string) => {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return label;
  return words.map((word, index) => (
    <span
      key={`${word}-${index}`}
      className="oc-dropdown__word"
      style={{ '--word-delay': `${index * 45}ms` } as React.CSSProperties}
    >
      {word}
    </span>
  ));
};

const Dropdown: React.FC<DropdownProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  emptyLabel,
  disabled,
  open,
  onOpenChange,
  onToggle,
  menuPlacement = 'below',
  usePortal,
  portalRect,
  portalRef,
  containerRef,
  className,
  menuClassName,
  renderToggle,
}) => {
  const { t } = useI18n();
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = isControlled ? open : internalOpen;
  const wrapRef = useRef<HTMLDivElement>(null);
  const localMenuRef = useRef<HTMLDivElement>(null);
  const menuRef = portalRef || localMenuRef;
  const [autoPortalRect, setAutoPortalRect] = React.useState<PortalRect | null>(null);
  const useLayout = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

  const setOpenState = useCallback((nextOpen: boolean) => {
    if (!isControlled) setInternalOpen(nextOpen);
    if (onOpenChange) onOpenChange(nextOpen);
  }, [isControlled, onOpenChange]);

  const selected = useMemo(() => options.find(opt => String(opt.value) === String(value)) || null, [options, value]);
  const display = selected ? selected.label : '';
  const hasValue = Boolean(selected);
  const resolvedPlaceholder = placeholder === undefined ? t('common.select') : placeholder;
  const resolvedEmptyLabel = emptyLabel === undefined ? t('common.noOptions') : emptyLabel;

  useEffect(() => {
    if (!isOpen) return;
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      const wrapEl = (containerRef && containerRef.current) || wrapRef.current;
      const menuEl = menuRef.current;
      if (!wrapEl) return;
      const inside = wrapEl.contains(t) || (!!menuEl && menuEl.contains(t));
      if (!inside) setOpenState(false);
    };
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node;
      const wrapEl = (containerRef && containerRef.current) || wrapRef.current;
      const menuEl = menuRef.current;
      if (!wrapEl) return;
      const inside = wrapEl.contains(t) || (!!menuEl && menuEl.contains(t));
      if (!inside) setOpenState(false);
    };
    document.addEventListener('mousedown', onDocPointer, { passive: true });
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    document.addEventListener('focusin', onFocusIn as EventListener, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('focusin', onFocusIn as EventListener);
    };
  }, [containerRef, isOpen, menuRef, setOpenState]);

  const shouldPortal = usePortal !== false;

  const computePortalRect = useCallback(() => {
    const wrapEl = (containerRef && containerRef.current) || wrapRef.current;
    if (!wrapEl) return;
    const rect = wrapEl.getBoundingClientRect();
    let top = rect.bottom + 6;
    if (menuPlacement === 'above') {
      const menuHeight = menuRef.current?.offsetHeight || 232;
      top = rect.top - 6 - menuHeight;
    }
    if (top < 8) top = 8;
    setAutoPortalRect({ left: rect.left, top, width: rect.width });
  }, [containerRef, menuPlacement, menuRef]);

  useLayout(() => {
    if (!isOpen || !shouldPortal || portalRect) return;
    computePortalRect();
    const raf = requestAnimationFrame(() => computePortalRect());
    const onScroll = () => computePortalRect();
    const onResize = () => computePortalRect();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [computePortalRect, isOpen, portalRect, shouldPortal]);

  const labelId = id ? `${id}-label` : undefined;
  const showLabel = Boolean(label);
  const resolvedLabelId = showLabel ? labelId : undefined;

  const toggleMenu = () => setOpenState(!isOpen);
  const openMenu = () => setOpenState(true);
  const closeMenu = () => setOpenState(false);

  const toggleProps: React.HTMLAttributes<HTMLElement> = {
    id,
    'aria-haspopup': 'listbox',
    'aria-expanded': isOpen,
    'aria-labelledby': resolvedLabelId,
    onKeyDown: (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      }
    },
  };

  const resolvedPortalRect = portalRect || autoPortalRect;
  const fallbackPortalRect = (() => {
    if (!shouldPortal || resolvedPortalRect) return null;
    const wrapEl = (containerRef && containerRef.current) || wrapRef.current;
    if (!wrapEl) return null;
    const rect = wrapEl.getBoundingClientRect();
    let top = rect.bottom + 6;
    if (menuPlacement === 'above') {
      const menuHeight = menuRef.current?.offsetHeight || 232;
      top = rect.top - 6 - menuHeight;
    }
    return { left: rect.left, top, width: rect.width };
  })();
  const effectivePortalRect = resolvedPortalRect || fallbackPortalRect;
  const menu = (
    <div
      ref={menuRef}
      className={`oc-dropdown__menu ${shouldPortal && effectivePortalRect ? 'is-portal' : ''} ${isOpen ? 'open' : ''} ${menuPlacement} ${menuClassName || ''}`}
      role="listbox"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setOpenState(false);
        }
      }}
      style={shouldPortal && effectivePortalRect ? { position: 'fixed', left: effectivePortalRect.left, top: effectivePortalRect.top, width: effectivePortalRect.width } : undefined}
    >
      {options.length === 0 && (
        <div className="oc-dropdown__item" role="option" aria-disabled="true">
          {renderDropdownLabel(resolvedEmptyLabel)}
        </div>
      )}
      {options.map((opt) => (
        <div
          key={String(opt.value)}
          role="option"
          aria-selected={String(opt.value) === String(value)}
          aria-disabled={opt.disabled}
          className={`oc-dropdown__item ${String(opt.value) === String(value) ? 'selected' : ''} ${opt.disabled ? 'disabled' : ''}`}
          onClick={() => {
            if (opt.disabled) return;
            onChange(opt.value);
            setOpenState(false);
          }}
        >
          {renderDropdownLabel(opt.label)}
        </div>
      ))}
    </div>
  );

  return (
    <div
      ref={containerRef || wrapRef}
      className={`oc-dropdown ${isOpen ? 'has-open' : ''} ${hasValue ? 'has-value' : ''} ${disabled ? 'is-disabled' : ''} ${className || ''}`}
    >
      {renderToggle ? (
        renderToggle({
          isOpen,
          disabled,
          display,
          placeholder: resolvedPlaceholder,
          toggleProps,
          openMenu,
          closeMenu,
          toggleMenu,
        })
      ) : (
        <button
          {...toggleProps}
          type="button"
          className="oc-dropdown__toggle"
          onClick={() => {
            if (disabled) return;
            const nextOpen = !isOpen;
            if (onToggle) onToggle(nextOpen);
            setOpenState(nextOpen);
          }}
          disabled={disabled}
        >
          <span className={`oc-dropdown__value ${display ? '' : 'is-placeholder'}`}>{display || resolvedPlaceholder}</span>
          <span className="oc-dropdown__arrow" aria-hidden />
        </button>
      )}
      {showLabel && resolvedLabelId && <span id={resolvedLabelId} className="oc-dropdown__label">{label}</span>}
      {shouldPortal && isOpen && effectivePortalRect ? createPortal(menu, document.body) : menu}
    </div>
  );
};

export default Dropdown;
